/** Staff panels for a client: e-signatures, consents, organizers and return filings. */
import { FormAnswers, FormAnswersBody, type FormDetail } from "@/components/form-answers";
import { formatCents, JURISDICTIONS, parseDollars, REFUND_AUTHORITIES, visibleQuestions, type OrganizerDefinition, type Question } from "@clientflow/shared";
import { ask, askText } from "@/components/dialogs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { boxStyle, PdfPages } from "@/components/pdf-view";
import { DocumentPicker } from "@/components/document-picker";
import { Empty, Loading, Pager, usePaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { defaultTaxYear, formatDate, formatDateTime, localToday, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import type { Doc, Engagement } from "@/lib/types";

const pill = "rounded-full px-2.5 py-0.5 text-xs font-medium";
const tone = {
  wait: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  review: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200",
  done: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
  off: "bg-muted text-muted-foreground",
};
const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

// --- E-signatures -------------------------------------------------------------------

type SigRequest = {
  id: string;
  title: string;
  kind: "simple" | "irs_8879";
  method: "click" | "wet" | "kba";
  status: "pending" | "in_review" | "completed" | "declined" | "cancelled";
  createdAt: string;
  signedDocumentId: string | null;
  consentPurpose: string | null;
  signers: Array<{ name: string; status: string; signedAt: string | null }>;
};

const SIG_STATUS: Record<SigRequest["status"], [string, string]> = {
  pending: ["Awaiting signature", tone.wait],
  in_review: ["Signed — review required", tone.review],
  completed: ["Signed", tone.done],
  declined: ["Declined", tone.off],
  cancelled: ["Cancelled", tone.off],
};

export const CONSENT_LABELS: Record<string, string> = {
  tax_return_use: "Use of tax return information",
  tax_return_disclosure: "Disclosure of tax return information",
  marketing: "Marketing other services",
  ai_processing: "AI-assisted processing",
  benchmarking: "Anonymous benchmarking",
};

type PortalUser = { id: string; userId?: string; name?: string; access: string; active: boolean };

export type PlacedBox = { key: number; signerUserId: string; page: number; x: number; y: number; width: number; height: number; kind: "signature" | "initials" | "date" | "text"; label?: string };

const BOX_SIZE: Record<PlacedBox["kind"], [number, number]> = { signature: [0.3, 0.04], initials: [0.08, 0.03], date: [0.15, 0.025], text: [0.25, 0.025] };
const BOX_LABEL: Record<PlacedBox["kind"], string> = { signature: "Signature", initials: "Initials", date: "Date", text: "Text" };
export const SIGNER_COLORS = ["#2563eb", "#c2410c", "#7c3aed", "#0f766e"];
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Click on a page to drop a box for the chosen signer; drag to move; × to remove. */
function BoxPlacer({ docUrl, signers, boxes, setBoxes }: { docUrl: string; signers: Array<{ userId: string; name: string }>; boxes: PlacedBox[]; setBoxes: (b: PlacedBox[]) => void }) {
  const [who, setWho] = useState(signers[0]?.userId ?? "");
  const [kind, setKind] = useState<PlacedBox["kind"]>("signature");
  const [label, setLabel] = useState("");
  const drag = useRef<{ key: number; startX: number; startY: number; bx: number; by: number; w: number; h: number } | null>(null);
  const signer = signers.find((s) => s.userId === who) ? who : signers[0]?.userId ?? "";
  const color = (uid: string) => SIGNER_COLORS[Math.max(0, signers.findIndex((s) => s.userId === uid)) % SIGNER_COLORS.length];
  const missing = signers.filter((s) => !boxes.some((b) => b.signerUserId === s.userId && b.kind === "signature"));
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="box-who">Box for</Label>
          <select id="box-who" className={select} value={signer} onChange={(e) => setWho(e.target.value)}>
            {signers.map((s) => (
              <option key={s.userId} value={s.userId}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-1" role="radiogroup" aria-label="Box type">
          {(Object.keys(BOX_LABEL) as PlacedBox["kind"][]).map((k) => (
            <Button key={k} type="button" size="sm" role="radio" aria-checked={kind === k} variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}>
              {BOX_LABEL[k]}
            </Button>
          ))}
        </div>
        {kind === "text" && <Input aria-label="Text box label" placeholder="Label, e.g. Occupation" className="w-48" value={label} onChange={(e) => setLabel(e.target.value)} />}
      </div>
      <p className="text-xs text-muted-foreground">
        Click on the page where the {BOX_LABEL[kind].toLowerCase()} should go. Drag a box to move it.
        {missing.length > 0 && <span className="text-amber-700 dark:text-amber-300"> Still needs a signature box: {missing.map((m) => m.name).join(", ")}.</span>}
      </p>
      <div className="max-h-[70vh] overflow-y-auto rounded-md bg-muted/40 p-2">
        <PdfPages
          url={docUrl}
          onPageClick={(page, x, y) => {
            if (!signer) return;
            const [w, h] = BOX_SIZE[kind];
            setBoxes([...boxes, { key: Date.now() + Math.random(), signerUserId: signer, page, kind, label: kind === "text" ? label.trim() || undefined : undefined, width: w, height: h, x: clamp(x - w / 2, 0, 1 - w), y: clamp(y - h / 2, 0, 1 - h) }]);
          }}
          overlay={(page) =>
            boxes
              .filter((b) => b.page === page)
              .map((b) => (
                <div
                  key={b.key}
                  className="absolute flex cursor-move select-none items-center justify-between gap-1 rounded-sm border-2 px-1 text-[10px] font-medium text-white"
                  style={{ ...boxStyle(b), borderColor: color(b.signerUserId), background: `${color(b.signerUserId)}cc` }}
                  onPointerDown={(e) => {
                    const page = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
                    drag.current = { key: b.key, startX: e.clientX, startY: e.clientY, bx: b.x, by: b.y, w: page.width, h: page.height };
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    const d = drag.current;
                    if (!d || d.key !== b.key) return;
                    setBoxes(boxes.map((x) => (x.key === b.key ? { ...x, x: clamp(d.bx + (e.clientX - d.startX) / d.w, 0, 1 - x.width), y: clamp(d.by + (e.clientY - d.startY) / d.h, 0, 1 - x.height) } : x)));
                  }}
                  onPointerUp={() => (drag.current = null)}
                  title={`${BOX_LABEL[b.kind]} — ${signers.find((s) => s.userId === b.signerUserId)?.name ?? ""}`}
                >
                  <span className="truncate">
                    {b.label ?? BOX_LABEL[b.kind]} · {signers.find((s) => s.userId === b.signerUserId)?.name.split(" ")[0]}
                  </span>
                  <button
                    type="button"
                    aria-label="Remove box"
                    className="rounded px-0.5 hover:bg-black/20"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setBoxes(boxes.filter((x) => x.key !== b.key))}
                  >
                    ×
                  </button>
                </div>
              ))
          }
        />
      </div>
    </div>
  );
}

export function SignaturesPanel({ clientId, me, portalUsers }: { clientId: string; me: Me; portalUsers: PortalUser[] }) {
  const { data, isLoading } = useQuery<SigRequest[]>({ queryKey: ["/api/signature-requests?clientId=", clientId] });
  const { data: docs } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", clientId] });
  const canEdit = hasPermission(me, "documents.write");
  const signable = (docs ?? []).filter((d) => d.visibility !== "internal" && /\.pdf$/i.test(d.filename));
  const people = portalUsers.filter((p) => p.active && p.access === "full" && p.userId);
  const [form, setForm] = useState({ documentId: "", kind: "simple", title: "", consentPurpose: "", signers: [] as string[] });
  const [boxes, setBoxes] = useState<PlacedBox[]>([]);
  const [placing, setPlacing] = useState(false);
  const { data: settings } = useQuery<{ allow8879WithoutIdentityCheck?: boolean }>({ queryKey: ["/api/firm/settings"] });
  const clickSign = form.kind === "simple" || !!settings?.allow8879WithoutIdentityCheck;
  // Boxes belong to chosen signers only.
  const liveBoxes = boxes.filter((b) => form.signers.includes(b.signerUserId));
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", "/api/signature-requests", {
        clientId,
        documentId: form.documentId,
        kind: form.kind,
        title: form.title,
        signerUserIds: form.signers,
        consentPurpose: form.consentPurpose || undefined,
        fields: clickSign && placing ? liveBoxes.map(({ key: _k, label, ...b }) => ({ ...b, ...(label ? { label } : {}) })) : [],
      }),
    onSuccess: () => {
      setForm({ documentId: "", kind: "simple", title: "", consentPurpose: "", signers: [] });
      setBoxes([]);
      setPlacing(false);
    },
  });
  const act = useMutation({ mutationFn: (v: { id: string; path: string; body?: object }) => api.send("POST", `/api/signature-requests/${v.id}/${v.path}`, v.body) });
  const paged = usePaged(data ?? []);

  return (
    <div className="space-y-4">
      {canEdit && (
        <form
          className="space-y-3 rounded-lg border border-border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <p className="text-sm font-medium">Request a signature</p>
          {people.length === 0 ? (
            <p className="text-sm text-muted-foreground">Invite the client to the portal with full access first. Each signer signs in to their own account.</p>
          ) : signable.length === 0 ? (
            <p className="text-sm text-muted-foreground">First upload the PDF to be signed on the Documents tab, shared with the client.</p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="sig-doc">Document</Label>
                  <DocumentPicker
                    id="sig-doc"
                    required
                    docs={signable}
                    allowNone={false}
                    placeholder="Select a PDF"
                    value={form.documentId}
                    onChange={(id) => {
                      setForm({ ...form, documentId: id });
                      // Boxes belong to one document's pages.
                      setBoxes([]);
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sig-title">Title shown to the client</Label>
                  <Input id="sig-title" required placeholder="2026 Engagement Letter" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sig-kind">Document type</Label>
                  <select id="sig-kind" className={select} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, consentPurpose: "" })}>
                    <option value="simple">Engagement letter, consent or proposal (electronic signature)</option>
                    <option value="irs_8879">IRS e-file authorization, Form 8879 / 8878 (print, sign and upload)</option>
                  </select>
                </div>
                {form.kind === "simple" && (
                  <div className="space-y-1">
                    <Label htmlFor="sig-consent">§7216 consent form</Label>
                    <select id="sig-consent" className={select} value={form.consentPurpose} onChange={(e) => setForm({ ...form, consentPurpose: e.target.value })}>
                      <option value="">No</option>
                      {Object.entries(CONSENT_LABELS)
                        // The AI consent is sent from the client's AI section, with the firm's wording.
                        .filter(([k]) => k !== "ai_processing")
                        .map(([k, v]) => (
                        <option key={k} value={k}>
                          Yes — {v}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <fieldset className="space-y-1">
                <legend className="text-sm font-medium">Signers</legend>
                {people.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.signers.includes(p.userId!)}
                      onChange={(e) => setForm({ ...form, signers: e.target.checked ? [...form.signers, p.userId!] : form.signers.filter((x) => x !== p.userId) })}
                    />
                    {p.name}
                  </label>
                ))}
              </fieldset>
              {form.kind === "irs_8879" && !clickSign && (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  The IRS requires an identity check (KBA) to sign Form 8879 remotely. Until a KBA provider is connected, clients print, sign and upload the form, and you review it here. An owner can change this in Settings → Firm.
                </p>
              )}
              {form.kind === "irs_8879" && clickSign && (
                <p className="rounded-md border border-accent/60 bg-accent/10 px-3 py-2 text-xs">
                  Your firm allows clients to click-sign Form 8879 without the identity check. The signed copy will say so.
                </p>
              )}
              {clickSign && form.documentId && form.signers.length > 0 && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={placing} onChange={(e) => setPlacing(e.target.checked)} /> Place signature boxes on the pages (1040, 8879, state return…)
                  </label>
                  {placing && (
                    <BoxPlacer
                      docUrl={`/api/documents/${form.documentId}/download`}
                      signers={form.signers.map((uid) => ({ userId: uid, name: people.find((p) => p.userId === uid)?.name ?? "Signer" }))}
                      boxes={liveBoxes}
                      setBoxes={setBoxes}
                    />
                  )}
                  {!placing && <p className="text-xs text-muted-foreground">If no signature fields are placed, the client signs once and a signature page is appended to the document.</p>}
                </div>
              )}
              <Button
                type="submit"
                disabled={create.isPending || !form.signers.length || (placing && clickSign && form.signers.some((uid) => !liveBoxes.some((b) => b.signerUserId === uid && b.kind === "signature")))}
              >
                Send for signature
              </Button>
              {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
            </>
          )}
        </form>
      )}
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>No signature requests have been sent.</Empty>
      ) : (
        <>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {paged.items.map((r) => (
            <li key={r.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{r.title}</p>
                <p className="text-xs text-muted-foreground">
                  {r.signers.map((g) => `${g.name}${g.status === "signed" ? " (signed)" : g.status === "declined" ? " (declined)" : ""}`).join(", ")} · Sent {formatDate(r.createdAt)}
                  {r.consentPurpose && ` · §7216 consent: ${CONSENT_LABELS[r.consentPurpose]}`}
                </p>
              </div>
              <span className={`${pill} ${SIG_STATUS[r.status][1]}`}>{SIG_STATUS[r.status][0]}</span>
              {r.signedDocumentId && (
                <a className="text-sm text-primary hover:underline" href={`/api/documents/${r.signedDocumentId}/download`}>
                  Signed copy
                </a>
              )}
              {canEdit && r.status === "in_review" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => act.mutate({ id: r.id, path: "review", body: { decision: "accept" } })}>
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const note = await askText({ title: "Return the signed form to the client", label: "Reason for return (visible to the client)", required: true, confirmLabel: "Return to client" });
                      if (note !== null) act.mutate({ id: r.id, path: "review", body: { decision: "reopen", note } });
                    }}
                  >
                    Return to client
                  </Button>
                </>
              )}
              {canEdit && (r.status === "pending" || r.status === "in_review") && (
                <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Cancel this signature request?", description: "The client will no longer be able to sign it.", confirmLabel: "Cancel request", cancelLabel: "Keep", destructive: true })) && act.mutate({ id: r.id, path: "cancel" })}>
                  Cancel
                </Button>
              )}
            </li>
          ))}
        </ul>
        <Pager paged={paged} noun="signature requests" />
        </>
      )}
    </div>
  );
}

// --- Consents ----------------------------------------------------------------------------

type Consent = { id: string; purpose: string; grantedAt: string; expiresAt: string | null; revokedAt: string | null; signerName: string; signatureRequestId: string | null };

export function ConsentsPanel({ clientId, me }: { clientId: string; me: Me }) {
  const { data, isLoading } = useQuery<Consent[]>({ queryKey: ["/api/consents?clientId=", clientId] });
  const canEdit = hasPermission(me, "clients.write");
  // Only an owner or admin may record a paper AI consent (it lets AI work with the client's data).
  const manager = me.role === "owner" || me.role === "admin";
  const purposes = Object.entries(CONSENT_LABELS).filter(([k]) => k !== "ai_processing" || manager);
  const [form, setForm] = useState({ purpose: "tax_return_use", grantedOn: "", expiresOn: "", signerName: "", documentId: "" });
  const { data: docs } = useQuery<Doc[]>({ queryKey: [`/api/documents?clientId=${clientId}`], enabled: canEdit });
  const add = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/consents", { clientId, purpose: form.purpose, grantedOn: form.grantedOn, expiresOn: form.expiresOn || null, signerName: form.signerName, ...(form.documentId ? { documentId: form.documentId } : {}) }),
    onSuccess: () => setForm({ ...form, grantedOn: "", expiresOn: "", signerName: "", documentId: "" }),
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/consents/${id}/revoke`) });
  const now = Date.now();
  const paged = usePaged(data ?? []);
  return (
    <div className="space-y-4">
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>No §7216 consents on file. Uses of tax return information beyond return preparation remain disabled for this client.</Empty>
      ) : (
        <>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {paged.items.map((c) => {
            const active = !c.revokedAt && (!c.expiresAt || Date.parse(c.expiresAt) > now);
            return (
              <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{CONSENT_LABELS[c.purpose] ?? c.purpose}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.signerName} · Signed {formatDate(c.grantedAt)} · {c.revokedAt ? `Withdrawn ${formatDate(c.revokedAt)}` : c.expiresAt ? `Expires ${formatDate(c.expiresAt)}` : "No expiration"}
                    {c.signatureRequestId ? " · e-signed" : " · on paper"}
                  </p>
                </div>
                <span className={`${pill} ${active ? tone.done : tone.off}`}>{active ? "Active" : c.revokedAt ? "Withdrawn" : "Expired"}</span>
                {canEdit && active && (
                  <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Record this consent as withdrawn?", description: "Uses of tax return information that depend on this consent stop immediately.", confirmLabel: "Record withdrawal", destructive: true })) && revoke.mutate(c.id)}>
                    Withdraw
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        <Pager paged={paged} noun="consents" />
        </>
      )}
      {canEdit && (
        <form
          className="grid gap-2 rounded-lg border border-border p-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <p className="text-sm font-medium sm:col-span-2">Record a paper-signed consent</p>
          <select aria-label="Purpose" className={select} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
            {purposes.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <Input aria-label="Signed by" placeholder="Signed by (full name)" required value={form.signerName} onChange={(e) => setForm({ ...form, signerName: e.target.value })} />
          <div className="space-y-1">
            <Label htmlFor="c-on">Signed on</Label>
            <Input id="c-on" type="date" required value={form.grantedOn} onChange={(e) => setForm({ ...form, grantedOn: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-exp">Expiration date (defaults to one year)</Label>
            <Input id="c-exp" type="date" value={form.expiresOn} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="c-doc">Signed copy (upload it to Documents first)</Label>
            <DocumentPicker id="c-doc" docs={docs} value={form.documentId} onChange={(id) => setForm({ ...form, documentId: id })} />
          </div>
          <Button type="submit" variant="outline" disabled={add.isPending} className="sm:col-span-2 sm:justify-self-start">
            Record consent
          </Button>
          {add.error && <p className="text-sm text-destructive sm:col-span-2">{errorMessage(add.error)}</p>}
        </form>
      )}
    </div>
  );
}

// --- Organizers ----------------------------------------------------------------------------

type OrganizerRow = { id: string; kind?: "organizer" | "intake"; title: string; taxYear: number; status: "open" | "submitted" | "cancelled"; submittedAt: string | null; prefilledFromId: string | null; progress: { answered: number; total: number } };
type OrganizerTemplate = { id: string; name: string; module: string; kind?: "organizer" | "intake"; isActive: boolean; returnTypes?: string[]; description?: string };
/** A submitted or in-progress questionnaire's answers, read-only, for staff (answers are the client's own). */
export function Responses({ id }: { id: string }) {
  return <FormAnswers id={id} />;
}

function ResponsesDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { data } = useQuery<FormDetail>({ queryKey: ["/api/organizers/", id] });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-3xl overflow-y-auto [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="pr-6">{data?.title ?? "Responses"}</DialogTitle>
        </DialogHeader>
        {!data ? <Loading /> : <FormAnswersBody data={data} />}
      </DialogContent>
    </Dialog>
  );
}

export function OrganizersPanel({ clientId, me, kind = "organizer" }: { clientId: string; me: Me; kind?: "organizer" | "intake" }) {
  const { data: all, isLoading } = useQuery<OrganizerRow[]>({ queryKey: ["/api/organizers?clientId=", clientId] });
  const data = all?.filter((o) => (o.kind ?? "organizer") === kind);
  const [viewing, setViewing] = useState<string | null>(null);
  const intake = kind === "intake";
  const { data: templates } = useQuery<OrganizerTemplate[]>({ queryKey: ["/api/organizer-templates"] });
  const { data: work } = useQuery<{ items: Engagement[] }>({ queryKey: ["/api/engagements?clientId=", clientId] });
  const canEdit = hasPermission(me, "requests.write");
  const [form, setForm] = useState({ templateId: "", taxYear: String(defaultTaxYear()), engagementId: "" });
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", "/api/organizers", intake ? { clientId, templateId: form.templateId } : { clientId, templateId: form.templateId, taxYear: Number(form.taxYear), engagementId: form.engagementId || undefined }),
  });
  const act = useMutation({ mutationFn: (v: { id: string; path: string }) => api.send("POST", `/api/organizers/${v.id}/${v.path}`) });
  const paged = usePaged(data ?? []);
  return (
    <div className="space-y-4">
      {canEdit && (
        <form
          className={`grid gap-2 rounded-lg border border-border p-4 ${intake ? "sm:grid-cols-[1fr_auto]" : "sm:grid-cols-[2fr_1fr_2fr_auto]"}`}
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <select aria-label="Organizer" required className={select} value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
            <option value="">{intake ? "Select an intake form to send" : "Select an organizer to send"}</option>
            {templates
              ?.filter((t) => t.isActive && (t.kind ?? "organizer") === kind)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
          {!intake && (
            <>
              <Input aria-label="Tax year" inputMode="numeric" value={form.taxYear} onChange={(e) => setForm({ ...form, taxYear: e.target.value })} />
              <select
                aria-label="Link to engagement"
                className={select}
                value={form.engagementId}
                onChange={(e) => {
                  // Linking a return picks the form made for that return type (S corporation, partnership…).
                  const rt = (work?.items.find((x) => x.id === e.target.value) as { returnType?: string | null } | undefined)?.returnType;
                  const match = rt ? templates?.find((t) => t.isActive && (t.kind ?? "organizer") === "organizer" && t.returnTypes?.includes(rt)) : undefined;
                  setForm({ ...form, engagementId: e.target.value, templateId: match?.id ?? form.templateId });
                }}
              >
                <option value="">Not linked to an engagement</option>
                {work?.items.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </>
          )}
          <Button type="submit" disabled={send.isPending}>
            Send
          </Button>
          {templates?.find((t) => t.id === form.templateId)?.description && <p className="text-xs text-muted-foreground sm:col-span-4">{templates.find((t) => t.id === form.templateId)!.description}</p>}
          {!intake && <p className="text-xs text-muted-foreground sm:col-span-4">Linking the return picks the form made for its type. The engagement advances automatically once the organizer and its document checklist are complete, and the preparer gets a task to review the answers.</p>}
          {send.error && <p className="text-sm text-destructive sm:col-span-4">{errorMessage(send.error)}</p>}
        </form>
      )}
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{intake ? "No intake forms have been sent." : "No organizers have been sent."}</Empty>
      ) : (
        <>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {paged.items.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{o.title}</p>
                <p className="text-xs text-muted-foreground">
                  {o.status === "submitted" ? `Submitted ${formatDateTime(o.submittedAt!)}` : `${o.progress.answered} of ${plural(o.progress.total, "question")} answered`}
                  {o.prefilledFromId && " · pre-filled from last year"}
                </p>
              </div>
              <span className={`${pill} ${o.status === "submitted" ? tone.done : o.status === "open" ? tone.wait : tone.off}`}>
                {o.status === "submitted" ? "Submitted" : o.status === "open" ? "Sent — awaiting client" : "Cancelled"}
              </span>
              {(o.progress.answered > 0 || (canEdit && o.status === "open" && o.kind !== "intake")) && (
                <Button size="sm" variant="outline" onClick={() => setViewing(o.id)}>
                  {o.progress.answered > 0 ? "View responses" : "Open"}
                </Button>
              )}
              {canEdit && o.status === "submitted" && (
                <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: o.id, path: "reopen" })}>
                  Reopen
                </Button>
              )}
              {canEdit && o.status === "open" && (
                <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: o.kind === "intake" ? "Cancel this intake form?" : "Cancel this organizer?", description: "The client will no longer see it.", confirmLabel: o.kind === "intake" ? "Cancel form" : "Cancel organizer", cancelLabel: "Keep", destructive: true })) && act.mutate({ id: o.id, path: "cancel" })}>
                  Cancel
                </Button>
              )}
            </li>
          ))}
        </ul>
        <Pager paged={paged} noun={intake ? "intake forms" : "organizers"} />
        </>
      )}
      {viewing && <ResponsesDialog id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// --- Return filings ------------------------------------------------------------------------

type Filing = {
  id: string;
  engagementId: string;
  taxYear: number;
  jurisdiction: string;
  form: string;
  isAmended: boolean;
  filingStatus: string | null;
  filingMethod: "efile" | "paper";
  extensionFiledOn: string | null;
  filedOn: string | null;
  acceptedOn: string | null;
  rejectedOn: string | null;
  refundCents: number | null;
  balanceDueCents: number | null;
  refundReceivedOn: string | null;
  clientReportedMissingAt: string | null;
  rejections?: Rejection[];
};

type Rejection = {
  id: string;
  rejectedOn: string;
  category: RejectionCategory;
  code: string;
  reason: string;
  requestId: string | null;
  correction: string | null;
  resubmittedOn: string | null;
};
type RejectionCategory = "identity" | "ip_pin" | "dependent" | "duplicate" | "form_error" | "other";

/** The usual causes of an e-file rejection, with what the client is typically asked for. */
export const REJECTION_CATEGORIES: Record<RejectionCategory, { label: string; ask: string }> = {
  identity: { label: "Name, SSN or date of birth mismatch", ask: "Please confirm your name, Social Security number and date of birth exactly as they appear on your Social Security card" },
  ip_pin: { label: "Missing or incorrect IP PIN", ask: "Please send your current Identity Protection PIN (IP PIN) from the IRS" },
  dependent: { label: "Dependent already claimed", ask: "Please confirm the details of your dependents and whether anyone else may have claimed them" },
  duplicate: { label: "Return already filed for this SSN", ask: "Please let us know if you or anyone else already filed a return for this year" },
  form_error: { label: "Form or math error (firm fix)", ask: "" },
  other: { label: "Other", ask: "" },
};

/** Records an e-file rejection; optionally sends the client a request for what is needed to refile. */
function RejectionDialog({ f, onClose }: { f: Filing; onClose: () => void }) {
  const [v, setV] = useState({ rejectedOn: localToday(), category: "identity" as RejectionCategory, code: "", reason: "", ask: true, askTitle: REJECTION_CATEGORIES.identity.ask });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", `/api/tax/filings/${f.id}/rejections`, {
        rejectedOn: v.rejectedOn,
        category: v.category,
        code: v.code,
        reason: v.reason,
        askClient: v.ask && v.askTitle.trim() ? { title: v.askTitle.trim() } : undefined,
      }),
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record e-file rejection</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rj-date">Rejected on</Label>
              <Input id="rj-date" type="date" required value={v.rejectedOn} onChange={(e) => setV({ ...v, rejectedOn: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rj-code">Rejection code</Label>
              <Input id="rj-code" placeholder="e.g. IND-031-04" value={v.code} onChange={(e) => setV({ ...v, code: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="rj-cat">Cause</Label>
            <select
              id="rj-cat"
              className={select}
              value={v.category}
              onChange={(e) => {
                const category = e.target.value as RejectionCategory;
                const ask = REJECTION_CATEGORIES[category].ask;
                setV({ ...v, category, askTitle: ask, ask: !!ask });
              }}
            >
              {Object.entries(REJECTION_CATEGORIES).map(([k, c]) => (
                <option key={k} value={k}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="rj-reason">Reason given</Label>
            <Textarea id="rj-reason" required rows={2} placeholder="The rejection message from the tax software" value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.ask} onChange={(e) => setV({ ...v, ask: e.target.checked })} /> Ask the client for what we need
          </label>
          {v.ask && <Input aria-label="Request to the client" value={v.askTitle} onChange={(e) => setV({ ...v, askTitle: e.target.value })} placeholder="What the client should provide" />}
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Record rejection
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Records how a rejection was corrected and when the return was filed again. */
function RefileDialog({ r, onClose }: { r: Rejection; onClose: () => void }) {
  const [v, setV] = useState({ correction: "", resubmittedOn: localToday() });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/tax/filing-rejections/${r.id}/resolve`, v), onSuccess: onClose });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record correction and refile</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Rejected {formatDate(r.rejectedOn)}
            {r.code && ` (${r.code})`}: {r.reason}
          </p>
          <div className="space-y-1">
            <Label htmlFor="rf-fix">What was corrected</Label>
            <Textarea id="rf-fix" required rows={2} value={v.correction} onChange={(e) => setV({ ...v, correction: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rf-date">Filed again on</Label>
            <Input id="rf-date" type="date" required value={v.resubmittedOn} onChange={(e) => setV({ ...v, resubmittedOn: e.target.value })} />
          </div>
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save and mark refiled
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FilingEditor({ f, canEdit }: { f: Filing; canEdit: boolean }) {
  const [v, setV] = useState({
    filingStatus: f.filingStatus ?? "",
    filingMethod: f.filingMethod,
    extensionFiledOn: f.extensionFiledOn ?? "",
    filedOn: f.filedOn ?? "",
    acceptedOn: f.acceptedOn ?? "",
    refund: f.refundCents !== null ? (f.refundCents / 100).toFixed(2) : "",
    due: f.balanceDueCents !== null ? (f.balanceDueCents / 100).toFixed(2) : "",
  });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const money = (s: string) => (s.trim() === "" ? null : parseDollars(s));
      return api.send("PATCH", `/api/tax/filings/${f.id}`, {
        filingStatus: v.filingStatus || null,
        filingMethod: v.filingMethod,
        extensionFiledOn: v.extensionFiledOn || null,
        filedOn: v.filedOn || null,
        acceptedOn: v.acceptedOn || null,
        refundCents: money(v.refund),
        balanceDueCents: money(v.due),
      });
    },
  });
  const date = (k: "extensionFiledOn" | "filedOn" | "acceptedOn", label: string) => (
    <div className="space-y-1">
      <Label htmlFor={`${f.id}-${k}`}>{label}</Label>
      <Input id={`${f.id}-${k}`} type="date" disabled={!canEdit} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
    </div>
  );
  return (
    <form
      className="grid gap-2 sm:grid-cols-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`${f.id}-fs`}>Filing status</Label>
        <select id={`${f.id}-fs`} disabled={!canEdit} className={select} value={v.filingStatus} onChange={(e) => setV({ ...v, filingStatus: e.target.value })}>
          <option value="">—</option>
          <option value="single">Single</option>
          <option value="mfj">Married filing jointly</option>
          <option value="mfs">Married filing separately</option>
          <option value="hoh">Head of household</option>
          <option value="qss">Qualifying surviving spouse</option>
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${f.id}-m`}>Filing method</Label>
        <select id={`${f.id}-m`} disabled={!canEdit} className={select} value={v.filingMethod} onChange={(e) => setV({ ...v, filingMethod: e.target.value as "efile" })}>
          <option value="efile">E-file</option>
          <option value="paper">Paper</option>
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${f.id}-r`}>Refund amount (per return)</Label>
        <Input id={`${f.id}-r`} inputMode="decimal" disabled={!canEdit} value={v.refund} onChange={(e) => setV({ ...v, refund: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${f.id}-d`}>Balance due (per return)</Label>
        <Input id={`${f.id}-d`} inputMode="decimal" disabled={!canEdit} value={v.due} onChange={(e) => setV({ ...v, due: e.target.value })} />
      </div>
      {date("extensionFiledOn", "Extension filed")}
      {date("filedOn", "Filed")}
      {date("acceptedOn", "Accepted")}
      {canEdit && (
        <div className="flex items-center gap-3 sm:col-span-4">
          <Button type="submit" size="sm" disabled={save.isPending}>
            Save
          </Button>
          {save.isSuccess && <span className="text-xs text-muted-foreground">Saved</span>}
          {save.error && <span className="text-sm text-destructive">{errorMessage(save.error)}</span>}
        </div>
      )}
    </form>
  );
}

/** Where a return stands, from the dates recorded on it. */
function filingStatus(f: Filing): [string, string] {
  if (f.acceptedOn) return [`Accepted ${formatDate(f.acceptedOn)}`, tone.done];
  if (f.rejectedOn && (!f.filedOn || f.rejectedOn >= f.filedOn)) return [`Rejected ${formatDate(f.rejectedOn)} — correction required`, tone.wait];
  if (f.filedOn) return [f.filingMethod === "efile" ? `E-filed ${formatDate(f.filedOn)} — awaiting acceptance` : `Paper-filed ${formatDate(f.filedOn)}`, tone.review];
  if (f.extensionFiledOn) return [`On extension since ${formatDate(f.extensionFiledOn)}`, tone.wait];
  return ["Not yet filed", tone.off];
}

/** One return as a summary line; its details open for editing on request. */
function FilingRow({ f, canEdit }: { f: Filing; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [refiling, setRefiling] = useState<Rejection | null>(null);
  const [label, cls] = filingStatus(f);
  const openRejection = f.rejections?.find((r) => !r.resubmittedOn);
  const canReject = canEdit && f.filingMethod === "efile" && !!f.filedOn && !f.acceptedOn && !openRejection;
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-sm font-medium">
          {f.taxYear} {f.jurisdiction === "federal" ? "Federal" : f.jurisdiction} Form {f.form}
          {f.isAmended && " (amended)"}
        </p>
        <span className={`${pill} ${cls}`}>{label}</span>
        {f.refundCents ? <span className="text-sm tabular text-muted-foreground">Refund {formatCents(f.refundCents)}</span> : null}
        {f.balanceDueCents ? <span className="text-sm tabular text-muted-foreground">Balance due {formatCents(f.balanceDueCents)}</span> : null}
        {canReject && (
          <Button size="sm" variant="outline" onClick={() => setRejecting(true)}>
            Record rejection
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Close" : canEdit ? "Edit" : "Details"}
        </Button>
      </div>
      {(f.clientReportedMissingAt && !f.refundReceivedOn) || f.refundReceivedOn || (!REFUND_AUTHORITIES[f.jurisdiction] && f.jurisdiction !== "federal") ? (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {f.clientReportedMissingAt && !f.refundReceivedOn && <span className={`${pill} ${tone.wait}`}>Client reported refund not received</span>}
          {f.refundReceivedOn && <span className={`${pill} ${tone.off}`}>Refund received {formatDate(f.refundReceivedOn)}</span>}
          {!REFUND_AUTHORITIES[f.jurisdiction] && f.jurisdiction !== "federal" && <span className="text-xs text-muted-foreground">Refund status lookup is not yet available for this state</span>}
        </div>
      ) : null}
      {!!f.rejections?.length && (
        <ul className="mt-2 space-y-1.5">
          {f.rejections.map((r) => (
            <li key={r.id} className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${r.resubmittedOn ? "border-border" : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"}`}>
              <span className="min-w-0 flex-1">
                <span className="font-medium">
                  Rejected {formatDate(r.rejectedOn)}
                  {r.code && ` · ${r.code}`} · {REJECTION_CATEGORIES[r.category].label}
                </span>
                <span className="block text-muted-foreground">{r.reason}</span>
                {r.resubmittedOn && (
                  <span className="block text-muted-foreground">
                    Corrected: {r.correction} · Filed again {formatDate(r.resubmittedOn)}
                  </span>
                )}
                {!r.resubmittedOn && r.requestId && <span className="block text-muted-foreground">A request was sent to the client.</span>}
              </span>
              {!r.resubmittedOn && canEdit && (
                <Button size="sm" onClick={() => setRefiling(r)}>
                  Record correction
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="mt-3 rounded-md bg-muted/40 p-3">
          <FilingEditor key={`${f.filedOn}-${f.rejectedOn}-${f.acceptedOn}`} f={f} canEdit={canEdit} />
        </div>
      )}
      {rejecting && <RejectionDialog f={f} onClose={() => setRejecting(false)} />}
      {refiling && <RefileDialog r={refiling} onClose={() => setRefiling(null)} />}
    </li>
  );
}

/** `loadingSize`: the room the list is expected to take while it loads (see `Loading`). */
export function FilingsPanel({ clientId, me, loadingSize }: { clientId: string; me: Me; loadingSize?: "box" }) {
  const { data, isLoading } = useQuery<Filing[]>({ queryKey: ["/api/tax/filings?clientId=", clientId] });
  const { data: work, isLoading: workLoading } = useQuery<{ items: Engagement[] }>({ queryKey: ["/api/engagements?clientId=", clientId] });
  const canEdit = hasPermission(me, "engagements.write");
  const taxWork = (work?.items ?? []).filter((e) => e.module === "tax");
  const [form, setForm] = useState({ engagementId: "", taxYear: String(defaultTaxYear()), jurisdiction: "federal", form: "1040", isAmended: false });
  const add = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/tax/filings", { ...form, taxYear: Number(form.taxYear) }),
  });
  // Newest tax year first, so the first page is this season's returns.
  const paged = usePaged([...(data ?? [])].sort((a, b) => b.taxYear - a.taxYear));
  // The form to add a return depends on the engagements, so both arrive before anything is shown.
  if (isLoading || workLoading) return <Loading size={loadingSize} />;
  return (
    <div className="space-y-4">
      {canEdit && taxWork.length > 0 && (
        <form
          className="grid gap-2 rounded-lg border border-border p-4 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <select
            aria-label="Tax engagement"
            required
            className={select}
            value={form.engagementId}
            onChange={(e) => {
              const rt = taxWork.find((x) => x.id === e.target.value)?.returnType;
              setForm({ ...form, engagementId: e.target.value, ...(form.jurisdiction === "federal" && rt ? { form: rt === "1120S" ? "1120-S" : rt } : {}) });
            }}
          >
            <option value="">Add a return to engagement</option>
            {taxWork.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <Input aria-label="Tax year" inputMode="numeric" value={form.taxYear} onChange={(e) => setForm({ ...form, taxYear: e.target.value })} />
          <select aria-label="Jurisdiction" className={select} value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value, form: e.target.value === "federal" ? "1040" : e.target.value === "MD" ? "502" : e.target.value === "VA" ? "760" : e.target.value === "DC" ? "D-40" : "" })}>
            {JURISDICTIONS.map((j) => (
              <option key={j} value={j}>
                {j === "federal" ? "Federal" : j}
              </option>
            ))}
          </select>
          <Input aria-label="Form" placeholder="Form" required value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })} />
          <Button type="submit" disabled={add.isPending}>
            Add return
          </Button>
          <label className="flex items-center gap-2 text-xs sm:col-span-5">
            <input type="checkbox" className="h-6 w-6 shrink-0" checked={form.isAmended} onChange={(e) => setForm({ ...form, isAmended: e.target.checked })} /> Amended return
          </label>
          {add.error && <p className="text-sm text-destructive sm:col-span-5">{errorMessage(add.error)}</p>}
        </form>
      )}
      {!data?.length ? (
        <Empty>No returns have been recorded. Add one return per jurisdiction (federal and each state) to track extensions, acceptance and refunds.</Empty>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {paged.items.map((f) => (
              <FilingRow key={f.id} f={f} canEdit={canEdit} />
            ))}
          </ul>
          <Pager paged={paged} noun="returns" />
        </>
      )}
    </div>
  );
}
